import React, { useState, useCallback, useMemo } from 'react'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Alert, AlertDescription } from '../ui/alert'
import { Progress } from '../ui/progress'
import { ChevronDownIcon, MoreHorizontalIcon, AlertTriangleIcon, CheckIcon, XIcon, RefreshCwIcon, FilterIcon } from 'lucide-react'

interface DataRecord {
  id: string
  title: string
  status: 'pending' | 'processing' | 'success' | 'error' | 'skipped'
  source: string
  priority: 'low' | 'medium' | 'high'
  issues: string[]
  preview?: {
    taskTitle: string
    description: string
    dueDate?: string
    tags: string[]
  }
  errorMessage?: string
}

interface BatchOperation {
  id: string
  type: 'import' | 'skip' | 'transform' | 'validate' | 'delete'
  label: string
  description: string
  requiresConfirmation?: boolean
  destructive?: boolean
}

interface FilterOptions {
  status: string[]
  priority: string[]
  hasIssues: boolean | null
  searchTerm: string
}

interface BatchOperationsProps {
  records: DataRecord[]
  onBatchAction: (action: string, recordIds: string[]) => Promise<void>
  onRecordUpdate: (recordId: string, updates: Partial<DataRecord>) => void
  isProcessing?: boolean
  processingProgress?: { current: number; total: number }
}

export function BatchOperations({ 
  records, 
  onBatchAction, 
  onRecordUpdate, 
  isProcessing = false,
  processingProgress 
}: BatchOperationsProps) {
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set())
  const [bulkAction, setBulkAction] = useState<string | null>(null)
  const [isConfirmingAction, setIsConfirmingAction] = useState(false)
  const [filters, setFilters] = useState<FilterOptions>({
    status: [],
    priority: [],
    hasIssues: null,
    searchTerm: ''
  })
  const [sortBy, setSortBy] = useState<'title' | 'status' | 'priority'>('title')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const batchOperations: BatchOperation[] = [
    { 
      id: 'import', 
      type: 'import', 
      label: 'Import Selected', 
      description: 'Create tasks from selected records',
      requiresConfirmation: true
    },
    { 
      id: 'skip', 
      type: 'skip', 
      label: 'Skip Selected', 
      description: 'Mark selected records as skipped'
    },
    { 
      id: 'validate', 
      type: 'validate', 
      label: 'Validate Selected', 
      description: 'Re-run validation on selected records'
    },
    { 
      id: 'transform', 
      type: 'transform', 
      label: 'Auto-Fix Issues', 
      description: 'Attempt to automatically fix common issues'
    },
    { 
      id: 'delete', 
      type: 'delete', 
      label: 'Delete Selected', 
      description: 'Remove selected records from import queue',
      requiresConfirmation: true,
      destructive: true
    }
  ]

  // Filter and sort records
  const filteredAndSortedRecords = useMemo(() => {
    let filtered = records.filter(record => {
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(record.status)) {
        return false
      }
      
      // Priority filter
      if (filters.priority.length > 0 && !filters.priority.includes(record.priority)) {
        return false
      }
      
      // Issues filter
      if (filters.hasIssues !== null) {
        const hasIssues = record.issues.length > 0
        if (filters.hasIssues && !hasIssues) return false
        if (!filters.hasIssues && hasIssues) return false
      }
      
      // Search term
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        return record.title.toLowerCase().includes(searchLower) ||
               record.source.toLowerCase().includes(searchLower) ||
               record.preview?.taskTitle.toLowerCase().includes(searchLower)
      }
      
      return true
    })

    // Sort records
    filtered.sort((a, b) => {
      let aValue: string | number = a[sortBy]
      let bValue: string | number = b[sortBy]
      
      if (sortBy === 'priority') {
        const priorityOrder = { low: 0, medium: 1, high: 2 }
        aValue = priorityOrder[a.priority]
        bValue = priorityOrder[b.priority]
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [records, filters, sortBy, sortDirection])

  const toggleSelection = (recordId: string) => {
    setSelectedRecords(prev => {
      const next = new Set(prev)
      if (next.has(recordId)) {
        next.delete(recordId)
      } else {
        next.add(recordId)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedRecords.size === filteredAndSortedRecords.length) {
      setSelectedRecords(new Set())
    } else {
      setSelectedRecords(new Set(filteredAndSortedRecords.map(r => r.id)))
    }
  }

  const handleBatchAction = async (actionId: string) => {
    const operation = batchOperations.find(op => op.id === actionId)
    if (!operation) return

    if (operation.requiresConfirmation) {
      setIsConfirmingAction(true)
      const message = operation.destructive 
        ? `Are you sure you want to ${operation.label.toLowerCase()}? This action cannot be undone.`
        : `${operation.description} for ${selectedRecords.size} records?`
      
      const confirmed = confirm(message)
      setIsConfirmingAction(false)
      
      if (!confirmed) return
    }

    try {
      await onBatchAction(actionId, Array.from(selectedRecords))
      setSelectedRecords(new Set()) // Clear selection after action
    } catch (error) {
      console.error('Batch action failed:', error)
    }
  }

  const getStatusIcon = (status: DataRecord['status']) => {
    switch (status) {
      case 'success': return <CheckIcon className="w-4 h-4 text-green-500" />
      case 'error': return <XIcon className="w-4 h-4 text-red-500" />
      case 'processing': return <RefreshCwIcon className="w-4 h-4 text-blue-500 animate-spin" />
      case 'skipped': return <div className="w-4 h-4 rounded-full bg-gray-300" />
      default: return <div className="w-4 h-4 rounded-full bg-gray-100" />
    }
  }

  const getStatusBadge = (status: DataRecord['status']) => {
    const variants = {
      pending: 'secondary',
      processing: 'default',
      success: 'default',
      error: 'destructive',
      skipped: 'outline'
    } as const
    
    return (
      <Badge variant={variants[status]} className="flex items-center gap-1">
        {getStatusIcon(status)}
        {status}
      </Badge>
    )
  }

  const getPriorityBadge = (priority: DataRecord['priority']) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    }
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[priority]}`}>
        {priority}
      </span>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filters and Search */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FilterIcon className="w-5 h-5" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Search</label>
              <Input
                placeholder="Search records..."
                value={filters.searchTerm}
                onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select
                value={filters.status.join(',')}
                onValueChange={(value) => setFilters(prev => ({ 
                  ...prev, 
                  status: value ? value.split(',') : []
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Priority</label>
              <Select
                value={filters.priority.join(',')}
                onValueChange={(value) => setFilters(prev => ({ 
                  ...prev, 
                  priority: value ? value.split(',') : []
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Issues</label>
              <Select
                value={filters.hasIssues?.toString() || ''}
                onValueChange={(value) => setFilters(prev => ({ 
                  ...prev, 
                  hasIssues: value === '' ? null : value === 'true'
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All records" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All records</SelectItem>
                  <SelectItem value="true">With issues</SelectItem>
                  <SelectItem value="false">No issues</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Batch Actions Bar */}
      {selectedRecords.size > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  {selectedRecords.size} record{selectedRecords.size !== 1 ? 's' : ''} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedRecords(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                {batchOperations.map(operation => (
                  <Button
                    key={operation.id}
                    variant={operation.destructive ? "destructive" : "default"}
                    size="sm"
                    onClick={() => handleBatchAction(operation.id)}
                    disabled={isProcessing || isConfirmingAction}
                  >
                    {operation.label} ({selectedRecords.size})
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Processing Progress */}
      {isProcessing && processingProgress && (
        <Card>
          <CardContent className="py-3">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Processing batch operation...</span>
                <span>{processingProgress.current} / {processingProgress.total}</span>
              </div>
              <Progress 
                value={(processingProgress.current / processingProgress.total) * 100} 
                className="w-full" 
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Records Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Records ({filteredAndSortedRecords.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="title">Title</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="priority">Priority</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedRecords.size === filteredAndSortedRecords.length && filteredAndSortedRecords.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Issues</TableHead>
                <TableHead>Preview</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No records match your filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRecords.has(record.id)}
                        onCheckedChange={() => toggleSelection(record.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{record.title}</div>
                        <div className="text-sm text-gray-500">Source: {record.source}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(record.status)}
                      {record.errorMessage && (
                        <div className="text-xs text-red-600 mt-1">{record.errorMessage}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {getPriorityBadge(record.priority)}
                    </TableCell>
                    <TableCell>
                      {record.issues.length > 0 ? (
                        <div className="space-y-1">
                          {record.issues.slice(0, 2).map((issue, index) => (
                            <div key={index} className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                              {issue}
                            </div>
                          ))}
                          {record.issues.length > 2 && (
                            <div className="text-xs text-gray-500">
                              +{record.issues.length - 2} more
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {record.preview ? (
                        <div className="max-w-xs">
                          <div className="font-medium text-sm truncate">{record.preview.taskTitle}</div>
                          <div className="text-xs text-gray-500 line-clamp-2">{record.preview.description}</div>
                          {record.preview.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {record.preview.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="text-xs bg-gray-100 px-1 rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No preview</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontalIcon className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onBatchAction('validate', [record.id])}>
                            Validate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onBatchAction('transform', [record.id])}>
                            Auto-fix
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => onBatchAction('skip', [record.id])}
                            className="text-yellow-600"
                          >
                            Skip
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => onBatchAction('delete', [record.id])}
                            className="text-red-600"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="py-3">
            <div className="text-2xl font-bold">{records.filter(r => r.status === 'pending').length}</div>
            <div className="text-sm text-gray-500">Pending</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="text-2xl font-bold">{records.filter(r => r.status === 'success').length}</div>
            <div className="text-sm text-gray-500">Success</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="text-2xl font-bold">{records.filter(r => r.status === 'error').length}</div>
            <div className="text-sm text-gray-500">Errors</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="text-2xl font-bold">{records.filter(r => r.issues.length > 0).length}</div>
            <div className="text-sm text-gray-500">With Issues</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="text-2xl font-bold">{records.length}</div>
            <div className="text-sm text-gray-500">Total</div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default BatchOperations